/**
 * Documents legaux, version francaise. Version de reference : le texte anglais
 * en est la traduction.
 *
 * Tout ce qui est affirme ici doit rester VRAI dans le code. Les durees, les
 * donnees traitees et les stockages decrits correspondent a l'implementation :
 * RAM uniquement cote serveur, aucune base de donnees, aucun cookie pose par le
 * jeu lui-meme (seule la regie publicitaire en depose, apres consentement).
 * Toute evolution (comptes, abonnement, mesure d'audience, publicite) demande
 * une mise a jour de ce fichier AVANT sa mise en service.
 */
import type { LegalCatalog } from './types.js';

const SIREN = '903 434 702';

const EDITEUR = 'Enki Corbin, entrepreneur individuel (micro-entreprise)';
const VILLE = 'Rennes, France';
const CONTACT = 'contact@cordev.fr';

export const FR: LegalCatalog = {
  legal: {
    title: 'Mentions légales',
    updated: 'En vigueur depuis le 10 août 2026',
    sections: [
      {
        heading: 'Éditeur',
        blocks: [
          `Le site retusch.io est édité par ${EDITEUR}, immatriculé sous le numéro ${SIREN}, établi à ${VILLE}.`,
          `Directeur de la publication : Enki Corbin. Contact : ${CONTACT}.`,
          'Aucun numéro de téléphone n’est mis à disposition. Toute demande, y compris urgente, doit passer par l’adresse de courriel ci-dessus, à laquelle il est répondu dans un délai raisonnable.',
        ],
      },
      {
        heading: 'Hébergement',
        blocks: [
          'Le site et ses fichiers sont hébergés par Cloudflare, Inc., 101 Townsend St, San Francisco, CA 94107, États-Unis.',
          'Le serveur de jeu, qui assure les parties en temps réel, est hébergé par Hetzner Online GmbH, Industriestr. 25, 91710 Gunzenhausen, Allemagne.',
        ],
      },
      {
        heading: 'Propriété intellectuelle',
        blocks: [
          'Le nom Retusch, le code du jeu, son interface et les illustrations qu’il utilise sont protégés. Toute reproduction ou réutilisation, totale ou partielle, sans autorisation écrite préalable est interdite.',
          'Les retouches réalisées par les joueurs pendant une partie restent la propriété de leur auteur. Elles ne sont ni conservées ni exploitées après la fin de la partie.',
        ],
      },
      {
        heading: 'Signalement',
        blocks: [
          `Tout contenu qui paraîtrait illicite peut être signalé à ${CONTACT}. Le signalement gagne à préciser la date, l’heure et le code du salon concerné, seuls éléments permettant de situer les faits.`,
          'Aucun contenu échangé pendant une partie n’étant conservé, un signalement postérieur à la fin de celle-ci ne peut donner lieu à aucune vérification.',
        ],
      },
    ],
  },

  privacy: {
    title: 'Confidentialité',
    updated: 'En vigueur depuis le 10 août 2026',
    sections: [
      {
        heading: 'En bref',
        blocks: [
          'Retusch se joue sans inscription. Tout ce qui circule pendant une partie vit dans la mémoire du serveur et disparaît lorsque le salon se ferme. Un compte est facultatif : il sert à conserver votre pseudonyme et votre avatar, et à accéder aux modes payants. Si vous en créez un, votre adresse e-mail est alors conservée, et elle seule.',
          'Le jeu est gratuit et financé par la publicité. C’est le seul élément qui dépose des cookies, et il ne se charge qu’après votre accord.',
          'Cette page décrit précisément ce qui est traité, pourquoi, et pendant combien de temps.',
        ],
      },
      {
        heading: 'Responsable du traitement',
        blocks: [
          `${EDITEUR}, établi à ${VILLE}. Pour toute question relative à vos données : ${CONTACT}.`,
        ],
      },
      {
        heading: 'Ce qui est traité pendant une partie',
        blocks: [
          'Les éléments suivants transitent par le serveur pour que le jeu fonctionne. Aucun n’est écrit sur disque, aucun n’est conservé après la partie.',
          {
            list: [
              'Le pseudonyme que vous choisissez, de 2 à 16 caractères. Il vous identifie auprès des autres joueurs du salon. Vous êtes libre de ne pas y mettre votre vrai nom, et il est recommandé de ne pas le faire.',
              'Vos retouches, transmises sous forme de tracés compressés. Le serveur les relaie sans jamais les interpréter.',
              'Vos messages de discussion, relayés aux autres joueurs du salon puis oubliés.',
              'Un jeton de session, identifiant technique tiré au hasard, qui permet de vous reconnecter si votre connexion tombe. Il vit 60 secondes après une déconnexion, puis disparaît.',
            ],
          },
          'Base légale : l’exécution du service que vous demandez en rejoignant une partie.',
        ],
      },
      {
        heading: 'Adresse IP',
        blocks: [
          'Votre adresse IP est nécessairement vue par le serveur pour établir la connexion. Elle est utilisée en mémoire pour une seule finalité : empêcher qu’un même point de connexion crée plus de cinq salons par minute, ce qui protège le service des abus.',
          'Elle est conservée dans une fenêtre glissante de 60 secondes, uniquement en mémoire vive. Elle n’est ni journalisée, ni écrite sur disque, ni transmise à quiconque.',
          'Base légale : l’intérêt légitime à protéger le service contre les usages abusifs.',
        ],
      },
      {
        heading: 'Journaux techniques',
        blocks: [
          'Le serveur consigne des événements de fonctionnement : code du salon, réglages de la partie, nombre de joueurs, durée. Ces journaux ne contiennent ni adresse IP, ni pseudonyme, ni contenu de dessin ou de message.',
          'Ils servent au diagnostic et à la sécurité, et sont conservés au maximum douze mois.',
        ],
      },
      {
        heading: 'Stockage dans votre navigateur',
        blocks: [
          'Le jeu lui-même ne dépose aucun cookie. Trois informations sont enregistrées localement dans votre navigateur, ne sortent jamais de votre appareil, et sont effaçables à tout moment en vidant les données du site :',
          {
            list: [
              'votre pseudonyme et votre préférence de son, pour ne pas les redemander à chaque visite,',
              'votre choix concernant la publicité, pour ne pas vous reposer la question,',
              'votre jeton de session en cours, pour permettre une reconnexion,',
              'deux identifiants de mesure tirés au hasard, décrits à la section « Mesure d’audience ».',
            ],
          },
          'La régie publicitaire, elle, dépose ses propres cookies si vous y avez consenti. Ils relèvent de sa politique, et sont décrits à la section suivante.',
        ],
      },
      {
        heading: 'Publicité',
        blocks: [
          'Le jeu est gratuit et financé par la publicité, fournie par Google au moyen de son offre destinée aux jeux en ligne.',
          'Aucun script publicitaire n’est chargé avant que vous n’ayez exprimé un choix. Si vous acceptez, Google peut déposer des cookies et lire des informations sur votre appareil afin de sélectionner et mesurer les annonces, y compris de façon personnalisée. Si vous refusez, la publicité reste diffusée mais devient non personnalisée : elle ne s’appuie alors sur aucun profil.',
          'Vous pouvez revenir sur votre choix à tout moment depuis la fenêtre de gestion du consentement, que vous pouvez rouvrir depuis le lien prévu à cet effet dans le message affiché lors de votre première visite.',
          'Les annonces n’apparaissent qu’entre deux moments de jeu, jamais pendant une phase de dessin, de recherche ou de révélation.',
          'Google agit comme responsable de traitement distinct pour ces opérations. Le détail des données qu’il traite et des durées qu’il applique relève de ses propres règles de confidentialité.',
        ],
      },
      {
        heading: 'Mesure d’audience',
        blocks: [
          'Le jeu mesure son propre usage, sans cookie, sans profilage et sans revendre quoi que ce soit. Deux dispositifs, tous deux limités à des statistiques agrégées.',
          'Le premier est interne : le serveur enregistre le déroulement des parties (partie lancée, manche terminée, recherche réussie ou non et en combien de temps, partie relancée, abandon et à quelle phase), ainsi que l’ouverture d’une page et le bouton cliqué. Ces événements servent à régler la difficulté et à comprendre où le jeu perd ses joueurs. Ils ne contiennent ni adresse IP, ni pseudonyme, ni dessin, ni message.',
          'Deux identifiants aléatoires y sont associés, générés par votre navigateur et dépourvus de toute information vous concernant : un identifiant de session, effacé à la fermeture de l’onglet, et un identifiant de visite conservé sur votre appareil, qui sert uniquement à savoir combien de personnes reviennent jouer un autre jour. Aucun des deux ne permet de vous identifier, ni de vous suivre sur d’autres sites. Vider les données du site les supprime.',
          'Ces événements sont conservés au maximum six mois, puis supprimés automatiquement.',
          'Le second dispositif est Cloudflare Web Analytics, qui compte les visites, les pages et les provenances. Il fonctionne sans cookie et sans identifiant persistant, et ne construit aucun profil.',
          'Aucune de ces mesures ne repose sur un traceur au sens de la réglementation applicable aux cookies : elles ne requièrent donc pas votre consentement préalable. Si cela devait changer, cette page serait mise à jour avant leur mise en service.',
        ],
      },
      {
        heading: 'Compte, si vous en créez un',
        blocks: [
          'Le compte est FACULTATIF. Sans lui, rien de ce qui suit ne vous concerne : aucune adresse n’est demandée, et le jeu fonctionne entièrement.',
          'Si vous en créez un, sont conservés :',
          {
            list: [
              'votre adresse e-mail, qui sert à vous identifier et à sécuriser l’accès,',
              'un mot de passe, jamais stocké tel quel mais sous forme d’empreinte irréversible (scrypt), sauf si vous passez par Google,',
              'votre pseudonyme et l’avatar choisi,',
              'la date de création et celle de la dernière connexion.',
            ],
          },
          'La connexion par Google nous transmet votre adresse e-mail et votre nom, et rien d’autre : ni vos contacts, ni vos fichiers, ni votre historique. Google agit comme responsable de traitement distinct pour cette opération.',
          'La vérification de l’adresse passe par Mailjet (Mailgun France SAS), qui reçoit votre adresse pour acheminer ce seul message. Il n’est pas utilisé pour de la prospection.',
          'Ces données sont conservées tant que le compte existe, puis supprimées dans les trente jours qui suivent sa suppression. Aucun profilage, aucune revente, aucun envoi commercial : la seule raison de vous écrire est de vérifier votre adresse ou de répondre à une demande de votre part.',
          'Base légale : l’exécution du contrat qui nous lie dès lors que vous créez un compte.',
        ],
      },
      {
        heading: 'Destinataires',
        blocks: [
          'Vos données ne sont ni vendues, ni louées, ni transmises à des tiers à des fins commerciales.',
          'Elles transitent techniquement par nos hébergeurs : Hetzner Online GmbH pour le serveur de jeu, en Allemagne, et Cloudflare, Inc. pour la distribution du site et la mesure de fréquentation. Si vous créez un compte, Mailjet (Mailgun France SAS) achemine le message de vérification, et Google Ireland Limited intervient si vous choisissez la connexion par Google.',
          'La régie publicitaire, Google Ireland Limited, reçoit pour sa part les données nécessaires à la diffusion des annonces, dans les conditions décrites à la section « Publicité » et sous réserve de votre consentement.',
          'Cloudflare et Google étant liés à des entités établies aux États-Unis, ces traitements peuvent impliquer un transfert hors de l’Union européenne, encadré par les garanties contractuelles et les décisions d’adéquation applicables.',
        ],
      },
      {
        heading: 'Durées de conservation',
        blocks: [
          {
            list: [
              'Pseudonyme, dessins, messages : le temps de la partie, en mémoire vive uniquement.',
              'Jeton de session : 60 secondes après une déconnexion.',
              'Adresse IP : 60 secondes, en mémoire vive uniquement.',
              'Journaux techniques, sans donnée personnelle : douze mois au maximum.',
              'Stockage local du navigateur : jusqu’à ce que vous l’effaciez.',
              'Cookies publicitaires, si vous les avez acceptés : durées fixées par Google, consultables dans ses règles de confidentialité.',
            ],
          },
        ],
      },
      {
        heading: 'Vos droits',
        blocks: [
          'Vous disposez d’un droit d’accès, de rectification, d’effacement, d’opposition, de limitation et de portabilité sur vos données. Vous pouvez les exercer à tout moment en écrivant à ' +
            CONTACT +
            '.',
          'En pratique, aucune donnée n’étant conservée après votre partie et aucun compte n’existant, il n’y a le plus souvent rien à extraire ni à effacer : quitter la partie suffit. Nous vous répondrons en ce sens si tel est le cas.',
          'Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de la Commission nationale de l’informatique et des libertés (CNIL), 3 place de Fontenoy, 75007 Paris.',
        ],
      },
      {
        heading: 'Mineurs',
        blocks: [
          'Le jeu n’est pas destiné aux enfants de moins de 15 ans, âge de la majorité numérique en France. En dessous de cet âge, l’utilisation suppose l’autorisation d’un titulaire de l’autorité parentale.',
          'Aucune vérification d’âge n’est effectuée, et aucune donnée permettant de connaître votre âge n’est collectée. Un parent qui constaterait une utilisation par un enfant peut nous écrire ; comme rien n’est conservé, l’arrêt de l’utilisation suffit à faire disparaître toute donnée.',
        ],
      },
      {
        heading: 'Évolutions',
        blocks: [
          'Cette politique sera mise à jour avant toute évolution qui changerait le traitement de vos données, notamment la mise en service de la publicité, d’une mesure d’audience, de comptes utilisateurs ou d’un abonnement. La date d’entrée en vigueur en haut de page fait foi.',
        ],
      },
    ],
  },

  terms: {
    title: 'Conditions d’utilisation',
    updated: 'En vigueur depuis le 10 août 2026',
    sections: [
      {
        heading: 'Objet',
        blocks: [
          `Les présentes conditions régissent l’utilisation du jeu Retusch, accessible à l’adresse retusch.io et édité par ${EDITEUR}.`,
          'Utiliser le jeu vaut acceptation de ces conditions. Si vous ne les acceptez pas, n’utilisez pas le jeu.',
        ],
      },
      {
        heading: 'Accès au service',
        blocks: [
          'Le jeu est gratuit et se joue sans inscription. Il suffit d’un navigateur récent et d’une connexion à internet. Un compte n’est nécessaire que pour les modes payants et pour choisir son avatar.',
          'Le service est fourni en l’état, sans garantie de disponibilité, de continuité ni d’absence de défaut. Il peut être interrompu, modifié ou arrêté à tout moment, notamment pour maintenance, sans préavis ni indemnité.',
        ],
      },
      {
        heading: 'Règles de conduite',
        blocks: [
          'Le jeu met en relation des personnes autour d’un pseudonyme, d’un dessin et d’une discussion. Vous êtes seul responsable de ce que vous y publiez.',
          'Il est interdit, sans que cette liste soit limitative :',
          {
            list: [
              'de publier un contenu illicite, haineux, discriminatoire, violent, sexuel ou attentatoire à la dignité d’autrui, que ce soit par le pseudonyme, le dessin ou la discussion,',
              'de harceler, menacer ou importuner un autre joueur,',
              'de publier des données personnelles, les vôtres ou celles d’un tiers,',
              'de diffuser de la publicité, un contenu promotionnel ou un lien non sollicité,',
              'de perturber délibérément le service, d’en contourner les limitations ou d’automatiser son utilisation.',
            ],
          },
          'Aucune modération préalable n’est exercée : les contenus circulent directement entre joueurs et ne sont pas conservés. L’accès au service peut être restreint ou interrompu, sans préavis, à l’encontre de quiconque enfreindrait ces règles.',
        ],
      },
      {
        heading: 'Propriété intellectuelle',
        blocks: [
          'Le jeu, son code, son interface et les illustrations qu’il utilise appartiennent à l’éditeur. Ils ne peuvent être copiés, modifiés ni réutilisés sans autorisation écrite.',
          'Vous conservez vos droits sur les retouches que vous réalisez. En les traçant, vous acceptez qu’elles soient montrées aux autres joueurs de la partie pendant celle-ci. Elles ne sont ni conservées ni réutilisées par l’éditeur après la fin de la partie.',
        ],
      },
      {
        heading: 'Publicité',
        blocks: [
          'Le jeu est gratuit et financé par la publicité. Les annonces apparaissent uniquement entre deux moments de jeu, jamais pendant une phase de dessin, de recherche ou de révélation.',
          'Contourner l’affichage des annonces par un moyen technique n’est pas interdit, mais prive le service de sa seule source de financement.',
        ],
      },
      {
        heading: 'Mineurs',
        blocks: [
          'Le jeu n’est pas destiné aux enfants de moins de 15 ans. En dessous de cet âge, son utilisation suppose l’autorisation d’un titulaire de l’autorité parentale, à qui il revient de surveiller l’usage qui en est fait, en particulier de la discussion écrite.',
        ],
      },
      {
        heading: 'Responsabilité',
        blocks: [
          'L’éditeur ne peut être tenu responsable des contenus publiés par les joueurs, ni des dommages résultant d’une interruption du service, d’une perte de partie en cours ou d’une incompatibilité de votre matériel.',
          'Rien dans ces conditions n’écarte la responsabilité qui ne peut légalement l’être.',
        ],
      },
      {
        heading: 'Modification des conditions',
        blocks: [
          'Ces conditions peuvent être modifiées, notamment lors de l’ajout de comptes utilisateurs, d’un abonnement ou de la publicité. La version applicable est celle publiée sur cette page, dont la date d’entrée en vigueur figure en haut.',
        ],
      },
      {
        heading: 'Droit applicable',
        blocks: [
          'Les présentes conditions sont soumises au droit français. À défaut de résolution amiable, tout litige relève de la compétence des tribunaux français.',
          `Pour toute réclamation : ${CONTACT}.`,
        ],
      },
    ],
  },
};
